import { useParams, Link } from 'react-router-dom';
import { useEffect, type FC } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories } from '@/mocks/data';

const detailImages: Record<string, string> = {
  'getting-started-guide': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-getting-started-guide&orientation=landscape',
  'platform-overview': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-platform-overview&orientation=landscape',
  'advanced-features-guide': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-advanced-features-guide&orientation=landscape',
  'resource-download-guide': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-resource-download-guide&orientation=landscape',
  'common-questions-answers': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-common-questions-answers&orientation=landscape',
  'security-privacy-guide': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-security-privacy-guide&orientation=landscape',
  'mobile-app-guide': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-mobile-app-guide&orientation=landscape',
  'team-collaboration-best-practices': 'https://readdy.ai/api/search-image?query=Professional%20editorial%20content%20illustration%20with%20warm%20minimalist%20modern%20design%2C%20clean%20digital%20platform%20aesthetic%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20elements%2C%20magazine%20quality%20photography&width=1200&height=600&seq=detail-team-collaboration-best-practices&orientation=landscape',
};

const ArticlePage: FC = () => {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.slug !== slug)
    .slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-400" />
          <p className="mt-4 text-foreground-600">文章未找到</p>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === article.category)?.name || article.category;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '17c.com',
    },
    inLanguage: 'zh-Hans',
  };

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 17c.com`,
          url: `https://www.17c.com/article/${slug}`,
          description: article.excerpt,
          inLanguage: 'zh-Hans',
        }}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Link
              to={`/category/${article.category}`}
              className="text-xs font-medium text-accent-600 bg-accent-50 px-3 py-1 rounded-full hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {categoryName}
            </Link>
            <span className="text-xs text-foreground-400">{article.date}</span>
            <span className="text-xs text-foreground-400">{article.readTime}</span>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight">
            {article.title}
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-3 pt-4 border-t border-background-200/70">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-semibold">
              17
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-900">{article.author}</p>
              <p className="text-xs text-foreground-500">官方认证作者</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {article.tags.map((tag) => (
              <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded">
                {tag}
              </span>
            ))}
          </div>
        </header>

        <div className="relative w-full h-64 md:h-96 mb-10 rounded-lg overflow-hidden">
          <img
            src={detailImages[article.slug] || detailImages['getting-started-guide']}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="prose-custom"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <footer className="mt-12 pt-8 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-foreground-950">这篇文章对您有帮助吗？</p>
              <p className="text-xs text-foreground-500 mt-1">在 17c.com 探索更多精彩内容</p>
            </div>
            <Link
              to="/category/user-guide"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始使用 17c.com
            </Link>
          </div>
        </footer>
      </article>

      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-foreground-500">{ra.date}</span>
                    <span className="text-xs text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full font-medium">
                      {ra.readTime}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                    {ra.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArticlePage;